import React, { useState, useEffect } from 'react';
import {
    Form,
    Input,
    Select,
    Row,
    Col,
    Button,
    Space,
    Modal,
    DatePicker,
    InputNumber
} from 'antd';
import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';
import { WalletGroup } from '../../../../types/WalletGroup'


const { TextArea } = Input;
const { Option } = Select;


interface Ledgers {
    ledger_id: number;
    ledger_first_name: string;
    ledger_last_name: string;
    ledger_name: string;
    ledger_occupation: string;
    ledger_tds: string;
    ledger_reserve_fund: string;
    wallet_other: string;
    wallet_tds: string;
    wallet_reserve_fund: string;
}

type WalletTotal = {
    wallet_type: string;
    total_amount: number;
};




interface WithdrawalModelProps {
    visible: boolean;
    onCancel: () => void;
    onCreate: (data: any) => void; // Callback after successful submit
    initialWithdrawalValues?: {
        wallet_id?: number;
        wallet_ledger_id?: string;
        wallet_type?: string;
        wallet_category?: string;
        wallet_remark?: string;
        wallet_amount?: string;
    } | null;
    isEditWithdrawalMode?: boolean;
    LedgerData: {
        ledger_id: string;
        ledger_first_name: string;
        ledger_last_name: string;
    }[];
}

const WithdrawalModel: React.FC<WithdrawalModelProps> = ({
    visible,
    onCancel,
    onCreate,
    initialWithdrawalValues = null,
    isEditWithdrawalMode = false,
    LedgerData = []
}) => {
    const [form] = Form.useForm();
    const [isModalClose, setIsModalClose] = useState(true);
    const [walletbalance, setWalletBalance] = useState<WalletGroup[]>([]);

    const [walletGroups, setWalletGroups] = useState<WalletTotal[]>([
        { wallet_type: "TDS", total_amount: 0 },
        { wallet_type: "Reserve Fund", total_amount: 0 },
        { wallet_type: "Other", total_amount: 0 }
    ]);


    // balance state
    const [tdsBalance, setTdsBalance] = useState(0);
    const [reserveBalance, setReserveBalance] = useState(0);
    const [otherBalance, setOtherBalance] = useState(0);


    // Determine if submit button should be disabled based on form validation
    const isSubmitDisabled = () => {
        const errors = form.getFieldsError();
        return errors.some(({ errors }) => errors.length > 0);
    };



    useEffect(() => {
        if (walletbalance.length) {
            const values: any = {};
            walletbalance.forEach(w => {
                values[`wallet_${w.wallet_type.replace(/\s+/g, '_')}`] = w.final_balance;
            });
            form.setFieldsValue(values);
        }
    }, [walletbalance, form]);

    async function fetchWalletBalance(ledger_id: number) {
        try {
            const response = await apiRequest({
                method: 'get',
                url: '/wallet-ledgers',
                params: { ledger_id }
            });

            const walletData: WalletGroup[] = response.walletGroups;

            const tds = walletData.find(w => w.wallet_type.toLowerCase() === 'tds')?.final_balance || 0;
            const reserve = walletData.find(w => w.wallet_type.toLowerCase() === 'reserve fund')?.final_balance || 0;
            const other = walletData.find(w => w.wallet_type.toLowerCase() === 'other')?.final_balance || 0;

            setTdsBalance(tds);
            setReserveBalance(reserve);
            setOtherBalance(other);

            setWalletBalance(response.walletGroups);
            // ---- update walletGroups for summary ----
            setWalletGroups([
                { wallet_type: "TDS", total_amount: Number(tds) },
                { wallet_type: "Reserve Fund", total_amount: Number(reserve) },
                { wallet_type: "Other", total_amount: Number(other) }
            ]);

            // 👇 show values in input boxes
            form.setFieldsValue({
                wallet_tds_amount: tds,
                wallet_reserve_amount: reserve,
                wallet_other_amount: other
            });

        } catch (err) {
            showError("Failed to load wallet ledger data");
        }
    }





    const handleAddWallet = async (values: any) => {
        try {
            // Build the wallet_withdrawal_amounts array
            const wallet_withdrawal_amounts = walletbalance.map(wallet => ({
                type: wallet.wallet_type.toLowerCase(), // 'tds', 'reserve fund', etc.
                amount: Number(values[`wallet_${wallet.wallet_type.replace(/\s+/g, '_')}`] || 0)
            }));

            const dataVal = {
                wallet_ledger_id: values.wallet_ledger_id,
                wallet_remark: values.wallet_remark,
                wallet_withdrawal_amounts, // <-- send as array
                ...(initialWithdrawalValues?.wallet_id && { wallet_id: initialWithdrawalValues.wallet_id }),
            };

            const url = isEditWithdrawalMode ? 'edit-wallet' : 'add-wallet';
            const method = isEditWithdrawalMode ? 'put' : 'post';

            const data = await apiRequest({ method, url, data: dataVal });

            onCreate(data);
            showSuccess('Amount withdrawal successfully!');
            form.resetFields();

            if (isModalClose) onCancel();
        } catch (error) {
            showError(`Failed to ${isEditWithdrawalMode ? 'update' : 'create'} wallet`);
        }
    };


    // When modal opens or mode changes, set/reset form fields accordingly
    useEffect(() => {
        if (visible && isEditWithdrawalMode && initialWithdrawalValues) {
            form.setFieldsValue({
                wallet_ledger_id: initialWithdrawalValues.wallet_ledger_id,
                wallet_type: initialWithdrawalValues.wallet_type,
                wallet_category: initialWithdrawalValues.wallet_category,
                wallet_remark: initialWithdrawalValues.wallet_remark,
                wallet_amount: initialWithdrawalValues.wallet_amount,
            });
        } else if (visible) {
            form.resetFields();
        }
    }, [visible, isEditWithdrawalMode, initialWithdrawalValues, form]);


    return (
        <Modal
            title={isEditWithdrawalMode ? 'Edit Withdrawal' : 'Add Withdrawal'}
            visible={visible}
            onCancel={onCancel}
            footer={null}
            width="90%"
            destroyOnHidden
            centered
            maskClosable={false}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleAddWallet}
                autoComplete="off"
            >
                <Row gutter={16}>
                    {/* ledgers */}
                    <Col span={12}>
                        <Form.Item
                            name="wallet_ledger_id"
                            className='px-8'
                            label={
                                <span>
                                    Select Ledger
                                </span>
                            }
                            rules={[{ required: true, message: 'Please select a ledger' }]}
                        >
                            <Select
                                placeholder="Select ledger"
                                onChange={(value) => {
                                    fetchWalletBalance(value);
                                    form.setFieldsValue({ wallet_ledger_id: value }); // just update form
                                }}
                                // value={selectedLedger}
                                showSearch
                                optionFilterProp="children"
                            >
                                {LedgerData.map((l) => (
                                    <Option key={l.ledger_id} value={l.ledger_id}>
                                        {l.ledger_first_name} {l.ledger_last_name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
                {walletbalance.length > 0 && (
                    <Row gutter={16}>
                        {walletbalance.map((wallet) => (
                            <Col span={12} key={wallet.wallet_type}>
                                <Form.Item
                                    name={`wallet_${wallet.wallet_type.replace(/\s+/g, '_')}`}
                                    className="px-8"
                                    label={`${wallet.wallet_type.charAt(0).toUpperCase() + wallet.wallet_type.slice(1)} Amount`}
                                    rules={[
                                        { required: true, message: `Please enter the ${wallet.wallet_type} amount` },
                                        {
                                            validator: (_, value) =>
                                                value && value > 0
                                                    ? Promise.resolve()
                                                    : Promise.reject(new Error('Amount must be greater than zero')),
                                        },
                                    ]}
                                >
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        parser={(value?: string) => {
                                            const parsed = value?.replace(/[^\d.]/g, '') ?? '';
                                            return parsed === '' ? 0 : parseFloat(parsed);
                                        }}

                                    />
                                </Form.Item>
                            </Col>
                        ))}
                    </Row>
                )}


                {walletbalance.length > 0 && (
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="wallet_remark"
                                className='px-8'
                                label={
                                    <span>
                                        Remark&nbsp;
                                        <span style={{ fontSize: '12px', color: '#888' }}>
                                            (Max 255 characters)
                                        </span>
                                    </span>
                                }
                                rules={[
                                    { required: true, message: 'Please enter the wallet remark' },
                                    { max: 255, message: 'Maximum 255 characters allowed' },
                                ]}
                            >
                                <Input.TextArea
                                    showCount
                                    maxLength={255}
                                    placeholder="e.g., wallet remark"
                                    autoSize={{ minRows: 3, maxRows: 6 }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <div style={{ background: "#f5f5f5", padding: 15, borderRadius: 6, marginBottom: 20 }}>
                                <h3>Wallet Summary</h3>
                                {walletbalance.map((g) => (
                                    <p key={g.wallet_type}>
                                        <strong style={{ textTransform: "capitalize" }}>{g.wallet_type}:</strong> ₹{g.final_balance}
                                    </p>
                                ))}

                                <hr />
                                <p>
                                    <strong>Total:</strong> ₹
                                    {walletbalance
                                        .reduce((sum, g) => sum + Number(g.final_balance || 0), 0)
                                        .toFixed(2)}
                                </p>
                            </div>
                        </Col>
                    </Row>
                )}




                <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Space>
                        <Button onClick={onCancel}>Cancel</Button>

                        <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(true)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default WithdrawalModel;
